import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type {
  DeliveryAddressDirectoryEntry,
  InspectionTypeCode,
  MaterialItem,
  RegionMasterDto,
  RequestFormData,
  StringDesignSubType
} from '../../domain/types';
import { deliveryAddressesForRegion, emptyMaterial } from '../../domain/types';
import {
  INCH_FRACTION_SELECT_OPTIONS,
  inchesPartsToDecimal,
  type InchFraction
} from '../../domain/casingInches';
import {
  emptyCasing,
  emptyCombustor,
  emptyElectricMotor,
  emptyEspCable,
  emptyFlare,
  emptyKnockOut,
  emptyLinePipe,
  emptyPressureVessel,
  emptyPumpingUnit,
  emptyRegionFieldInfo,
  emptyRods,
  emptyStorageTank,
  emptyTransformer,
  emptyWellInfo,
  isGenericMaterialKindUi,
  type MaterialLineKind,
  type RodLineDetails,
  type RodMaterialFamily,
  type SteelSuckerRodApiGrade,
  skipsMaterialLogisticsSection,
  usesCasingMaterialForm,
  usesStructuredEquipmentForm
} from '../../domain/procurementRequest';
import { MaterialStructuredSpecFields } from './MaterialStructuredSpecFields';
import type { WellMaster } from '../../domain/wells';
import { regionDisplayName } from '../../domain/wellRegions';
import { wellMasterToFormWellInfo } from '../../domain/wells';
import { AFES_API_URL, MATERIAL_SKU_CONFIGURATION_URL } from '../../constants';
import { buildMaterialSku, mergeMaterialSkuPatterns } from '../../domain/materialSku';

/** Set true to show "Add material line" again (currently one line per request). */
const SHOW_ADD_MATERIAL_LINE = false;

const INSPECTION_OPTIONS: { code: InspectionTypeCode; label: string }[] = [
  { code: 'None', label: 'NONE' },
  { code: 'MillOnly', label: 'MILL ONLY' },
  { code: 'Emi', label: 'EMI' },
  { code: 'Other', label: 'OTHER' },
  { code: 'VtiSea', label: 'VTI/SEA' },
  { code: 'Fld', label: 'FLD' }
];

function toggleInspectionType(
  current: InspectionTypeCode[],
  code: InspectionTypeCode,
  checked: boolean
): InspectionTypeCode[] {
  if (code === 'None') {
    return checked ? ['None'] : [];
  }
  let next = current.filter((c) => c !== 'None');
  if (checked) {
    if (!next.includes(code)) {
      next = [...next, code];
    }
  } else {
    next = next.filter((c) => c !== code);
  }
  return next;
}

function updateMaterialAt(
  materials: MaterialItem[],
  index: number,
  patch: Partial<MaterialItem>
): MaterialItem[] {
  return materials.map((m, i) => (i === index ? { ...m, ...patch } : m));
}

const KIND_DEFAULT_NAME: Partial<Record<MaterialLineKind, string>> = {
  Tubular: 'Tubular',
  Tubing: 'Tubing',
  Casing: 'Casing',
  Rods: 'Rods',
  Transformers: 'Transformers',
  ElectricMotors: 'Electric motors',
  PumpingUnits: 'Pumping units',
  LinePipe: 'Line pipe',
  TanksPressureVesselsKnockOuts: 'Tanks / pressure vessels / knockouts',
  EspCables: 'ESP cables',
  FlaresCombustors: 'Flares / combustors',
  StorageTank: 'Storage tank',
  PressureVessel: 'Pressure vessel',
  KnockOut: 'Knock out',
  Flares: 'Flare',
  Combustor: 'Combustor'
};

function patchMaterialKind(materials: MaterialItem[], index: number, kind: MaterialLineKind): MaterialItem[] {
  const m = materials[index];
  const logistics: Pick<
    MaterialItem,
    'condition' | 'bandIfUsed' | 'tested' | 'pressureNumber' | 'inspectionTypesRequired' | 'fullTruckload'
  > = {
    condition: m.condition,
    bandIfUsed: m.bandIfUsed,
    tested: m.tested,
    pressureNumber: m.pressureNumber,
    inspectionTypesRequired: [...(m.inspectionTypesRequired ?? [])],
    fullTruckload: m.fullTruckload
  };
  const skuReset = { sku: '', skuIsCustom: false };
  const q = m.quantity > 0 ? m.quantity : 1;
  if (usesCasingMaterialForm(kind)) {
    return updateMaterialAt(materials, index, {
      kind,
      stringDesignSubType: kind === 'StringDesign' ? (m.stringDesignSubType ?? 'SurfaceCasing') : undefined,
      casing: emptyCasing(),
      rods: undefined,
      transformer: undefined,
      electricMotor: undefined,
      pumpingUnit: undefined,
      linePipe: undefined,
      storageTank: undefined,
      pressureVessel: undefined,
      knockOut: undefined,
      espCable: undefined,
      flare: undefined,
      combustor: undefined,
      name: KIND_DEFAULT_NAME[kind] ?? kind,
      description: m.description,
      unit: m.unit,
      quantity: q,
      ...logistics,
      ...skuReset
    });
  }
  if (kind === 'Rods') {
    return updateMaterialAt(materials, index, {
      kind: 'Rods',
      stringDesignSubType: undefined,
      casing: undefined,
      rods: emptyRods(),
      transformer: undefined,
      electricMotor: undefined,
      pumpingUnit: undefined,
      linePipe: undefined,
      storageTank: undefined,
      pressureVessel: undefined,
      knockOut: undefined,
      espCable: undefined,
      flare: undefined,
      combustor: undefined,
      name: 'Rods',
      description: m.description,
      unit: m.unit,
      quantity: q,
      ...logistics,
      ...skuReset
    });
  }
  if (usesStructuredEquipmentForm(kind)) {
    return updateMaterialAt(materials, index, {
      kind,
      stringDesignSubType: undefined,
      casing: undefined,
      rods: undefined,
      transformer: kind === 'Transformers' ? emptyTransformer() : undefined,
      electricMotor: kind === 'ElectricMotors' ? emptyElectricMotor() : undefined,
      pumpingUnit: kind === 'PumpingUnits' ? emptyPumpingUnit() : undefined,
      linePipe: kind === 'LinePipe' ? emptyLinePipe() : undefined,
      storageTank: kind === 'StorageTank' ? emptyStorageTank() : undefined,
      pressureVessel: kind === 'PressureVessel' ? emptyPressureVessel() : undefined,
      knockOut: kind === 'KnockOut' ? emptyKnockOut() : undefined,
      espCable: kind === 'EspCables' ? emptyEspCable() : undefined,
      flare: kind === 'Flares' ? emptyFlare() : undefined,
      combustor: kind === 'Combustor' ? emptyCombustor() : undefined,
      name: KIND_DEFAULT_NAME[kind] ?? m.name,
      description: m.description,
      unit: m.unit || 'EA',
      quantity: q,
      ...logistics,
      ...skuReset
    });
  }
  if (isGenericMaterialKindUi(kind)) {
    return updateMaterialAt(materials, index, {
      kind,
      stringDesignSubType: undefined,
      casing: undefined,
      rods: undefined,
      transformer: undefined,
      electricMotor: undefined,
      pumpingUnit: undefined,
      linePipe: undefined,
      storageTank: undefined,
      pressureVessel: undefined,
      knockOut: undefined,
      espCable: undefined,
      flare: undefined,
      combustor: undefined,
      name: KIND_DEFAULT_NAME[kind] ?? m.name,
      description: m.description,
      unit: m.unit || 'EA',
      quantity: q,
      ...logistics,
      ...skuReset
    });
  }
  return updateMaterialAt(materials, index, {
    kind: 'Tubular',
    stringDesignSubType: undefined,
    casing: emptyCasing(),
    rods: undefined,
    transformer: undefined,
    electricMotor: undefined,
    pumpingUnit: undefined,
    linePipe: undefined,
    storageTank: undefined,
    pressureVessel: undefined,
    knockOut: undefined,
    espCable: undefined,
    flare: undefined,
    combustor: undefined,
    name: 'Tubular',
    quantity: q,
    ...logistics,
    ...skuReset
  });
}

export interface ProcurementRequestEditorProps {
  formData: RequestFormData;
  setFormData: Dispatch<SetStateAction<RequestFormData>>;
  wells: WellMaster[];
  /** Active regions from GET /api/regions (well region dropdowns + field lists). */
  activeRegions: RegionMasterDto[];
  deliveryAddresses: DeliveryAddressDirectoryEntry[];
  /** Keys from getSubmitValidationFieldErrors after a failed submit-for-approval. */
  submitFieldErrorKeys?: readonly string[];
  /** Load SKU format templates; when omitted, built-in defaults are used. */
  authFetch?: (url: string, init?: RequestInit) => Promise<Response>;
}

export function ProcurementRequestEditor({
  formData,
  setFormData,
  wells,
  activeRegions,
  deliveryAddresses,
  submitFieldErrorKeys,
  authFetch
}: ProcurementRequestEditorProps): ReactElement {
  const { requestor, materials, timeline } = formData;
  const [skuPatterns, setSkuPatterns] = useState<Record<MaterialLineKind, string> | null>(null);
  const [approvedAfes, setApprovedAfes] = useState<{ number: string; name: string }[]>([]);

  const fe = useCallback((key: string) => submitFieldErrorKeys?.includes(key) ?? false, [submitFieldErrorKeys]);
  const inv = useCallback((key: string) => (fe(key) ? ' field-invalid' : ''), [fe]);

  const currentWellId = formData.wellInfo.wellId.trim();
  const matchedWell = currentWellId
    ? wells.find((w) => w.id.trim().toLowerCase() === currentWellId.toLowerCase())
    : undefined;
  const orphanKey =
    currentWellId && !matchedWell
      ? currentWellId
      : '';

  const regionFromWell = (matchedWell?.region ?? formData.wellInfo.region ?? '').trim();
  const deliveryOptions = useMemo(
    () => deliveryAddressesForRegion(deliveryAddresses, regionFromWell),
    [deliveryAddresses, regionFromWell]
  );

  useEffect(() => {
    setFormData((prev) => {
      if (!prev.deliveryAddressId) {
        return prev;
      }
      if (deliveryOptions.some((d) => d.id === prev.deliveryAddressId)) {
        return prev;
      }
      return { ...prev, deliveryAddressId: '' };
    });
  }, [deliveryOptions, setFormData]);

  useEffect(() => {
    if (!authFetch) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await authFetch(AFES_API_URL);
        if (!r.ok) {
          return;
        }
        const rows = (await r.json()) as { number: string; name: string; status: string }[];
        if (!cancelled) {
          setApprovedAfes(rows.filter((a) => a.status === 'Approved'));
        }
      } catch {
        setApprovedAfes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  useEffect(() => {
    if (!authFetch) {
      setSkuPatterns(mergeMaterialSkuPatterns(null));
      return;
    }
    let cancelled = false;
    void (async () => {
      const r = await authFetch(MATERIAL_SKU_CONFIGURATION_URL);
      const body = (await r.json()) as { patternsByKind?: Record<string, string> };
      const patterns = r.ok ? mergeMaterialSkuPatterns(body.patternsByKind) : mergeMaterialSkuPatterns(null);
      if (!cancelled) {
        setSkuPatterns(patterns);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  useEffect(() => {
    if (!skuPatterns) {
      return;
    }
    setFormData((prev) => {
      let touched = false;
      const next = prev.materials.map((m) => {
        if (m.skuIsCustom) {
          return m;
        }
        const s = buildMaterialSku(m, skuPatterns);
        if (s === (m.sku ?? '')) {
          return m;
        }
        touched = true;
        return { ...m, sku: s };
      });
      if (!touched) {
        return prev;
      }
      return { ...prev, materials: next };
    });
  }, [materials, skuPatterns, setFormData]);

  const regionMeta = activeRegions.find((r) => r.code === regionFromWell);
  const fieldOptions = regionMeta?.fields ?? [];

  const selectValue =
    currentWellId === '' ? '' : matchedWell ? matchedWell.id : orphanKey ? `orphan:${orphanKey}` : '';

  return (
    <div className="procurement-request-form">
      <label className="full-width">
        Request type
        <select value="Material" disabled aria-readonly="true" title="Request type is fixed to Material">
          <option value="Material">Material</option>
        </select>
      </label>

      <h4 className="full-width form-section-title">Requestor</h4>
      <label className="full-width">
        Name
        <input
          placeholder="Name"
          value={requestor.name}
          onChange={(e) =>
            setFormData({ ...formData, requestor: { ...requestor, name: e.target.value } })
          }
        />
      </label>
      <label className="full-width">
        User id
        <input
          placeholder="User id"
          value={requestor.userId}
          onChange={(e) =>
            setFormData({ ...formData, requestor: { ...requestor, userId: e.target.value } })
          }
        />
      </label>
      <p className="hint full-width">
        Request scope follows the <strong>well&apos;s region</strong> (set when you pick a well below).
      </p>

      <h4 className="full-width form-section-title">Well</h4>
      <div className={`full-width labeled-field${inv('well') || inv('wellDirectory')}`}>
        <span className="labeled-field-label" id="well-picker-label">
          Well
        </span>
        <div className="well-picker-row" role="group" aria-labelledby="well-picker-label">
          <div className="well-picker-select">
            <select
              aria-labelledby="well-picker-label"
              value={selectValue}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setFormData((prev) => ({
                    ...prev,
                    wellInfo: emptyWellInfo(),
                    requestor: { ...prev.requestor, region: '' },
                    regionFieldInfo: emptyRegionFieldInfo()
                  }));
                  return;
                }
                if (v.startsWith('orphan:')) {
                  return;
                }
                const w = wells.find((x) => x.id === v);
                if (w) {
                  const wi = wellMasterToFormWellInfo(w);
                  setFormData((prev) => ({
                    ...prev,
                    wellInfo: wi,
                    requestor: { ...prev.requestor, region: wi.region },
                    regionFieldInfo: emptyRegionFieldInfo()
                  }));
                }
              }}
            >
              <option value="">Select a well…</option>
              {orphanKey && (
                <option value={`orphan:${orphanKey}`}>
                  {formData.wellInfo.wellId.trim()} (not in directory — save draft or pick another)
                </option>
              )}
              {wells.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.wellKey} — {w.wellName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <p className="hint full-width">
        Well id is the books master well (API number shown). Mongo well create is retired.
      </p>
      <p className={`full-width well-region-readout${inv('region')}`}>
        <strong>Region (from well):</strong>{' '}
        {regionFromWell ? regionDisplayName(regionFromWell, activeRegions) : '—'}
      </p>

      <h4 className="full-width form-section-title">Request details</h4>
      <label className={`full-width${inv('afeNumber')}`}>
        AFE#
        {approvedAfes.length > 0 ? (
          <select
            className="full-width"
            data-testid="afe-pick"
            value={formData.afeNumber}
            onChange={(e) => setFormData({ ...formData, afeNumber: e.target.value })}
          >
            <option value="">Select an Approved AFE</option>
            {approvedAfes.map((afe) => (
              <option key={afe.number} value={afe.number}>
                {afe.number} — {afe.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            placeholder="AFE#"
            className="full-width"
            value={formData.afeNumber}
            onChange={(e) => setFormData({ ...formData, afeNumber: e.target.value })}
            autoComplete="off"
          />
        )}
      </label>
      <label className={`full-width${inv('requestField')}`}>
        Field (from region catalog)
        <select
          value={formData.regionFieldInfo.id}
          disabled={!regionFromWell || fieldOptions.length === 0}
          onChange={(e) => {
            const id = e.target.value;
            const f = fieldOptions.find((x) => x.id === id);
            setFormData((prev) => ({
              ...prev,
              regionFieldInfo: {
                id,
                name: f?.name ?? '',
                county: f?.county ?? '',
                state: f?.state ?? '',
                closestCity: f?.closestCity ?? '',
                milesFromCity: f?.milesFromCity
              },
              wellInfo: { ...prev.wellInfo, field: f?.name ?? prev.wellInfo.field }
            }));
          }}
        >
          <option value="">
            {!regionFromWell
              ? 'Select a well first…'
              : fieldOptions.length === 0
                ? 'No fields configured for this region (ask an admin)'
                : 'Select field…'}
          </option>
          {fieldOptions.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name} — {f.county}, {f.state}
            </option>
          ))}
        </select>
      </label>
      {(() => {
        const sel = fieldOptions.find((f) => f.id === formData.regionFieldInfo.id);
        if (!sel) {
          return null;
        }
        return (
          <div className="full-width nested-card field-catalog-readout">
            <p className="hint" style={{ margin: '0 0 0.35rem' }}>
              From region field catalog
            </p>
            <p style={{ margin: '0.2rem 0' }}>
              <strong>Closest city:</strong> {sel.closestCity?.trim() || '—'}
            </p>
            <p style={{ margin: '0.2rem 0' }}>
              <strong>Miles from city:</strong>{' '}
              {sel.milesFromCity !== undefined && sel.milesFromCity !== null && !Number.isNaN(sel.milesFromCity)
                ? sel.milesFromCity
                : '—'}
            </p>
          </div>
        );
      })()}
      <label className={`full-width${inv('dateRequired')}`}>
        Date required
        <input
          type="date"
          value={timeline.requiredByDate ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              timeline: { ...timeline, requiredByDate: e.target.value || undefined }
            })
          }
        />
      </label>
      <label className={`full-width${inv('deliveryAddress')}`}>
        Delivery address
        <select
          value={formData.deliveryAddressId}
          disabled={!regionFromWell}
          title={
            regionFromWell
              ? 'Delivery locations scoped to this well’s region'
              : 'Choose a well first to list delivery addresses for that region'
          }
          onChange={(e) => setFormData({ ...formData, deliveryAddressId: e.target.value })}
        >
          <option value="">
            {!regionFromWell
              ? 'Select a well first…'
              : deliveryOptions.length === 0
                ? 'No delivery addresses for this region…'
                : 'Select delivery address…'}
          </option>
          {deliveryOptions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
              {d.category === 'WellPad' ? ' (well pad)' : ''}
            </option>
          ))}
        </select>
      </label>

      <h4 className={`full-width form-section-title${fe('materials') ? ' field-invalid' : ''}`}>Materials</h4>
      {materials.map((m: MaterialItem, idx: number) => (
        <div
          key={idx}
          className={`full-width material-row card nested-card${
            fe(`material-${idx}-quantity`) || fe(`material-${idx}-kind`) ? ' field-invalid' : ''
          }`}
        >
          {materials.length > 1 && (
            <div className="row spread material-line-actions">
              <span className="material-line-spacer" aria-hidden="true" />
              <button
                type="button"
                className="secondary danger-text"
                onClick={() =>
                  setFormData({
                    ...formData,
                    materials: materials.filter((_x: MaterialItem, i: number) => i !== idx)
                  })
                }
              >
                Remove line
              </button>
            </div>
          )}
          <label className="full-width">
            Material type
            <select
              value={m.kind}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  materials: patchMaterialKind(materials, idx, e.target.value as MaterialLineKind)
                })
              }
            >
              <option value="StringDesign">String design</option>
              <option value="Tubular">Tubular</option>
              <option value="Tubing">Tubing</option>
              <option value="Casing">Casing</option>
              <option value="Rods">Rods (sucker rod)</option>
              <option value="Transformers">Transformers</option>
              <option value="ElectricMotors">Electric motors</option>
              <option value="PumpingUnits">Pumping units</option>
              <option value="LinePipe">Line pipe</option>
              {m.kind === 'TanksPressureVesselsKnockOuts' ? (
                <option value="TanksPressureVesselsKnockOuts">Tanks, pressure vessels, knockouts (legacy)</option>
              ) : null}
              <option value="EspCables">ESP cables</option>
              {m.kind === 'FlaresCombustors' ? (
                <option value="FlaresCombustors">Flares / combustors (legacy)</option>
              ) : null}
              <option value="StorageTank">Storage tank</option>
              <option value="PressureVessel">Pressure vessel</option>
              <option value="KnockOut">Knock out</option>
              <option value="Flares">Flare</option>
              <option value="Combustor">Combustor</option>
            </select>
          </label>

          <label className="full-width">
            Material SKU
            <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <input
                className="full-width"
                style={{ flex: '1 1 12rem', minWidth: '8rem' }}
                value={m.sku ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    materials: updateMaterialAt(materials, idx, {
                      sku: e.target.value,
                      skuIsCustom: true
                    })
                  })
                }
                placeholder="Auto-generated from admin pattern; edit to override"
                title="Use Regenerate to apply the current format template from material fields"
              />
              <button
                type="button"
                className="secondary"
                onClick={() =>
                  setFormData({
                    ...formData,
                    materials: updateMaterialAt(materials, idx, {
                      skuIsCustom: false,
                      sku: skuPatterns ? buildMaterialSku(m, skuPatterns) : ''
                    })
                  })
                }
              >
                Regenerate
              </button>
            </div>
          </label>

          {m.kind === 'StringDesign' && (
            <label className={`full-width${fe(`material-${idx}-stringType`) ? ' field-invalid' : ''}`}>
              Casing type
              <select
                value={m.stringDesignSubType ?? 'SurfaceCasing'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    materials: updateMaterialAt(materials, idx, {
                      stringDesignSubType: e.target.value as StringDesignSubType
                    })
                  })
                }
              >
                <option value="SurfaceCasing">Surface casing</option>
                <option value="IntermediateCasing">Intermediate casing</option>
                <option value="ProductionString">Production string</option>
              </select>
            </label>
          )}

          {usesCasingMaterialForm(m.kind) && (
            <>
              <div
                className={`grid casing-grid${fe(`material-${idx}-casing`) ? ' casing-grid--invalid' : ''}`}
              >
                <label>
                  Net footage
                  <div className="field-with-unit">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={m.casing?.netFootage ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              netFootage: e.target.value === '' ? undefined : Number(e.target.value)
                            }
                          })
                        })
                      }
                    />
                    <span className="unit-suffix">ft</span>
                  </div>
                </label>
                <label>
                  Gross footage
                  <div className="field-with-unit">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={m.casing?.grossFootage ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              grossFootage: e.target.value === '' ? undefined : Number(e.target.value)
                            }
                          })
                        })
                      }
                    />
                    <span className="unit-suffix">ft</span>
                  </div>
                </label>
                <label className="full-width">
                  Plus make up thread loss (in.)
                  <div className="inch-input-row">
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="Inches"
                      aria-label="Plus make up thread loss, whole inches"
                      value={m.casing?.plusMakeupInchesWhole ?? ''}
                      onChange={(e) => {
                        const whole = e.target.value;
                        const frac = (m.casing?.plusMakeupInchesFraction ?? '') as InchFraction;
                        const dec = inchesPartsToDecimal(whole, frac);
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              plusMakeupInchesWhole: whole,
                              plusMakeupInchesFraction: frac,
                              plusMakeupThreadLoss: dec
                            }
                          })
                        });
                      }}
                    />
                    <select
                      aria-label="Plus make up thread loss, fraction of an inch"
                      value={m.casing?.plusMakeupInchesFraction ?? ''}
                      onChange={(e) => {
                        const frac = e.target.value as InchFraction;
                        const whole = m.casing?.plusMakeupInchesWhole ?? '';
                        const dec = inchesPartsToDecimal(whole, frac);
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              plusMakeupInchesFraction: frac,
                              plusMakeupThreadLoss: dec
                            }
                          })
                        });
                      }}
                    >
                      {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                        <option key={opt.value || 'none'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="inch-fraction-hint">in</span>
                  </div>
                </label>
                <label className="full-width">
                  Size / diameter (in.)
                  <div className="inch-input-row">
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="e.g. 4"
                      aria-label="Size diameter, whole inches"
                      value={m.casing?.sizeInchesWhole ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              sizeInchesWhole: e.target.value
                            }
                          })
                        })
                      }
                    />
                    <select
                      aria-label="Size diameter, fraction of an inch"
                      value={m.casing?.sizeInchesFraction ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: {
                              ...emptyCasing(),
                              ...m.casing,
                              sizeInchesFraction: e.target.value as InchFraction
                            }
                          })
                        })
                      }
                    >
                      {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                        <option key={`sz-${opt.value || 'none'}`} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="inch-fraction-hint">in</span>
                  </div>
                </label>
                <label>
                  Weight
                  <div className="field-with-unit">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      inputMode="decimal"
                      value={m.casing?.weight ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            casing: { ...emptyCasing(), ...m.casing, weight: e.target.value }
                          })
                        })
                      }
                    />
                    <span className="unit-suffix">lb</span>
                  </div>
                </label>
                <label>
                  Grade
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. P-110"
                    value={m.casing?.grade ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, {
                          casing: { ...emptyCasing(), ...m.casing, grade: e.target.value }
                        })
                      })
                    }
                  />
                </label>
                <label>
                  Thread
                  <input
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. BTC"
                    value={m.casing?.thread ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, {
                          casing: { ...emptyCasing(), ...m.casing, thread: e.target.value }
                        })
                      })
                    }
                  />
                </label>
                <label className="full-width">
                  Mfg. process
                  <select
                    value={m.casing?.mfgProcess ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, {
                          casing: {
                            ...emptyCasing(),
                            ...m.casing,
                            mfgProcess: e.target.value as '' | 'Erw' | 'Seamless'
                          }
                        })
                      })
                    }
                  >
                    <option value="">Select…</option>
                    <option value="Erw">ERW</option>
                    <option value="Seamless">Seamless</option>
                  </select>
                </label>
              </div>
            </>
          )}

          {m.kind === 'Rods' && (
            <div
              className={`full-width nested-card rod-spec-card${
                fe(`material-${idx}-rods`) ? ' field-invalid' : ''
              }`}
            >
              <h5 className="form-section-title" style={{ margin: '0 0 0.5rem' }}>
                Rod string
              </h5>
              <p className="hint" style={{ margin: '0 0 0.5rem' }}>
                Steel: API Grades C (low/medium load), D (high strength), K (corrosive). Fiberglass (FRP): light,
                corrosion-resistant, adds elasticity for stroke.
              </p>
              {(() => {
                const r: RodLineDetails = m.rods ?? emptyRods();
                const setRods = (patch: Partial<RodLineDetails>): void => {
                  setFormData({
                    ...formData,
                    materials: updateMaterialAt(materials, idx, { rods: { ...r, ...patch } })
                  });
                };
                return (
                  <>
                    <label className="full-width">
                      Rod material
                      <select
                        value={r.materialFamily}
                        onChange={(e) => {
                          const v = e.target.value as RodMaterialFamily;
                          setRods({ materialFamily: v, steelGrade: v === 'Steel' ? (r.steelGrade ?? 'C') : undefined });
                        }}
                      >
                        <option value="Steel">Steel</option>
                        <option value="Fiberglass">Fiberglass (FRP)</option>
                      </select>
                    </label>
                    {r.materialFamily === 'Steel' && (
                      <label className="full-width">
                        API steel grade
                        <select
                          value={r.steelGrade ?? 'C'}
                          onChange={(e) => setRods({ steelGrade: e.target.value as SteelSuckerRodApiGrade })}
                        >
                          <option value="C">C — low/medium loads</option>
                          <option value="D">D — high strength</option>
                          <option value="K">K — corrosive environments</option>
                        </select>
                      </label>
                    )}
                    <label className="full-width">
                      Rod diameter (in.)
                      <div className="inch-input-row">
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="e.g. 1"
                          aria-label="Rod diameter, whole inches"
                          value={r.rodDiameterInchesWhole}
                          onChange={(e) => setRods({ rodDiameterInchesWhole: e.target.value })}
                        />
                        <select
                          aria-label="Rod diameter, fraction of an inch"
                          value={r.rodDiameterInchesFraction}
                          onChange={(e) => setRods({ rodDiameterInchesFraction: e.target.value as InchFraction })}
                        >
                          {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                            <option key={`rod-${opt.value || 'none'}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <span className="inch-fraction-hint">in</span>
                      </div>
                    </label>
                    <label className="full-width">
                      Length (ft)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={r.lengthFeet ?? ''}
                        onChange={(e) =>
                          setRods({
                            lengthFeet: e.target.value === '' ? undefined : Number(e.target.value)
                          })
                        }
                      />
                    </label>
                    <label className="full-width">
                      Pony rods (length, in.) — optional
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={r.ponyRodsLengthInches ?? ''}
                        onChange={(e) =>
                          setRods({
                            ponyRodsLengthInches: e.target.value === '' ? undefined : Number(e.target.value)
                          })
                        }
                      />
                    </label>
                    <label className="full-width">
                      End connections (quantity)
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={r.endConnectionsQuantity ?? ''}
                        onChange={(e) =>
                          setRods({
                            endConnectionsQuantity: e.target.value === '' ? undefined : Number(e.target.value)
                          })
                        }
                      />
                    </label>
                    <label className="full-width">
                      Rod guides (quantity)
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={r.rodGuidesQuantity ?? ''}
                        onChange={(e) =>
                          setRods({
                            rodGuidesQuantity: e.target.value === '' ? undefined : Number(e.target.value)
                          })
                        }
                      />
                    </label>
                    <label className="full-width">
                      Polished rod length
                      <div className="polished-rod-length-row">
                        <div className="field-with-unit polished-rod-length-ft">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            placeholder="0"
                            aria-label="Polished rod length, feet"
                            value={r.polishedRodLengthFeet ?? ''}
                            onChange={(e) =>
                              setRods({
                                polishedRodLengthFeet: e.target.value === '' ? undefined : Number(e.target.value)
                              })
                            }
                          />
                          <span className="unit-suffix">ft</span>
                        </div>
                        <div className="inch-input-row polished-rod-length-in">
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="in whole"
                            aria-label="Polished rod length, whole inches"
                            value={r.polishedRodInchesWhole}
                            onChange={(e) => setRods({ polishedRodInchesWhole: e.target.value })}
                          />
                          <select
                            aria-label="Polished rod length, inch fraction"
                            value={r.polishedRodInchesFraction}
                            onChange={(e) => setRods({ polishedRodInchesFraction: e.target.value as InchFraction })}
                          >
                            {INCH_FRACTION_SELECT_OPTIONS.map((opt) => (
                              <option key={`pr-${opt.value || 'none'}`} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <span className="inch-fraction-hint">in</span>
                        </div>
                      </div>
                    </label>
                    <label className="full-width">
                      Quantity
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={m.quantity}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            materials: updateMaterialAt(materials, idx, {
                              quantity: e.target.value === '' ? 0 : Number(e.target.value)
                            })
                          })
                        }
                      />
                    </label>
                  </>
                );
              })()}
            </div>
          )}

          {usesStructuredEquipmentForm(m.kind) && (
            <div
              className={`full-width nested-card${inv(`material-${idx}-equipment`)}${inv(`material-${idx}-quantity`)}`}
              style={{ display: 'grid', gap: '0.5rem' }}
            >
              <h5 className="form-section-title" style={{ margin: '0 0 0.5rem' }}>
                Equipment specifications
              </h5>
              <MaterialStructuredSpecFields
                m={m}
                onPatch={(patch) =>
                  setFormData({ ...formData, materials: updateMaterialAt(materials, idx, patch) })
                }
                invalidClass={inv(`material-${idx}-equipment`)}
              />
              <label className={`full-width${fe(`material-${idx}-quantity`) ? ' field-invalid' : ''}`}>
                Quantity
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={m.quantity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      materials: updateMaterialAt(materials, idx, {
                        quantity: e.target.value === '' ? 0 : Number(e.target.value)
                      })
                    })
                  }
                />
              </label>
            </div>
          )}

          {isGenericMaterialKindUi(m.kind) && (
            <div
              className="full-width nested-card"
              style={{ display: 'grid', gap: '0.5rem' }}
            >
              <label className={`full-width${fe(`material-${idx}-name`) ? ' field-invalid' : ''}`}>
                Line title
                <input
                  value={m.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      materials: updateMaterialAt(materials, idx, { name: e.target.value })
                    })
                  }
                  placeholder="Short description of the equipment"
                />
              </label>
              <label className="full-width">
                Description
                <textarea
                  className="full-width"
                  rows={2}
                  value={m.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      materials: updateMaterialAt(materials, idx, { description: e.target.value })
                    })
                  }
                />
              </label>
              <div
                className="grid"
                style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}
              >
                <label className={fe(`material-${idx}-quantity`) ? 'field-invalid' : ''}>
                  Quantity
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={m.quantity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, {
                          quantity: e.target.value === '' ? 0 : Number(e.target.value)
                        })
                      })
                    }
                  />
                </label>
                <label className={fe(`material-${idx}-unit`) ? 'field-invalid' : ''}>
                  Unit
                  <input
                    value={m.unit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, { unit: e.target.value })
                      })
                    }
                    placeholder="EA, set, ft…"
                  />
                </label>
              </div>
            </div>
          )}

          {!skipsMaterialLogisticsSection(m.kind) && (
          <fieldset
            className={`full-width material-logistics-fieldset nested-card${
              fe(`material-${idx}-condition`) ||
              fe(`material-${idx}-band`) ||
              fe(`material-${idx}-tested`) ||
              fe(`material-${idx}-pressure`) ||
              fe(`material-${idx}-truckload`) ||
              fe(`material-${idx}-inspection`)
                ? ' field-invalid'
                : ''
            }`}
          >
            <legend>Condition, testing &amp; inspection</legend>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))' }}>
              <div className="radio-field">
                <span className="radio-field-label" id={`mat-${idx}-condition-label`}>
                  Condition
                </span>
                <div
                  className="radio-row"
                  role="radiogroup"
                  aria-labelledby={`mat-${idx}-condition-label`}
                >
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-condition`}
                      checked={m.condition === 'New'}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            condition: 'New',
                            bandIfUsed: ''
                          })
                        })
                      }
                    />
                    New
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-condition`}
                      checked={m.condition === 'Used'}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            condition: 'Used',
                            bandIfUsed: m.bandIfUsed
                          })
                        })
                      }
                    />
                    Used
                  </label>
                </div>
              </div>
              {m.condition === 'Used' && (
                <label>
                  (if used) BAND
                  <input
                    value={m.bandIfUsed ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, { bandIfUsed: e.target.value })
                      })
                    }
                  />
                </label>
              )}
              <div className="radio-field">
                <span className="radio-field-label" id={`mat-${idx}-tested-label`}>
                  Tested
                </span>
                <div
                  className="radio-row"
                  role="radiogroup"
                  aria-labelledby={`mat-${idx}-tested-label`}
                >
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-tested`}
                      checked={m.tested === true}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            tested: true,
                            pressureNumber: m.pressureNumber
                          })
                        })
                      }
                    />
                    Yes
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-tested`}
                      checked={m.tested === false}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, {
                            tested: false,
                            pressureNumber: ''
                          })
                        })
                      }
                    />
                    No
                  </label>
                </div>
              </div>
              {m.tested === true && (
                <label>
                  # PRESSURE
                  <input
                    value={m.pressureNumber ?? ''}
                    placeholder="e.g. PSI"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, { pressureNumber: e.target.value })
                      })
                    }
                  />
                </label>
              )}
              <div className="radio-field">
                <span className="radio-field-label" id={`mat-${idx}-fulltruck-label`}>
                  Full truckload
                </span>
                <div
                  className="radio-row"
                  role="radiogroup"
                  aria-labelledby={`mat-${idx}-fulltruck-label`}
                >
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-fulltruck`}
                      checked={m.fullTruckload === true}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, { fullTruckload: true })
                        })
                      }
                    />
                    Yes
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name={`mat-${idx}-fulltruck`}
                      checked={m.fullTruckload === false}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          materials: updateMaterialAt(materials, idx, { fullTruckload: false })
                        })
                      }
                    />
                    No
                  </label>
                </div>
              </div>
            </div>
            <p className="hint" style={{ margin: '0.5rem 0 0.25rem' }}>
              TYPE OF INSPECTION REQUIRED (check all that apply)
            </p>
            <div className="inspection-checkboxes">
              {INSPECTION_OPTIONS.map(({ code, label }) => (
                <label key={code} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={(m.inspectionTypesRequired ?? []).includes(code)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        materials: updateMaterialAt(materials, idx, {
                          inspectionTypesRequired: toggleInspectionType(
                            m.inspectionTypesRequired ?? [],
                            code,
                            e.target.checked
                          )
                        })
                      })
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          )}
        </div>
      ))}
      {SHOW_ADD_MATERIAL_LINE ? (
        <button
          type="button"
          className="secondary full-width"
          onClick={() => setFormData({ ...formData, materials: [...materials, emptyMaterial()] })}
        >
          Add material line
        </button>
      ) : null}

      <label className={`full-width${inv('businessJustification')}`}>
        Business justification
        <textarea
          className="full-width"
          placeholder="Describe the business need for this request"
          value={formData.businessJustification}
          onChange={(e) => setFormData({ ...formData, businessJustification: e.target.value })}
        />
      </label>

      <h4 className="full-width form-section-title">Timeline</h4>
      <label className="full-width">
        Expected delivery (optional)
        <input
          type="date"
          value={timeline.expectedDeliveryDate ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              timeline: { ...timeline, expectedDeliveryDate: e.target.value || undefined }
            })
          }
        />
      </label>
    </div>
  );
}
